import { useEffect, useState, type ReactNode } from 'react'
import { useAppDispatch } from '@/app/hooks'
import { createPlan, updatePlan } from '@/features/plans/plansSlice'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Field, TextInput } from '@/components/ui/Field'
import { useAsyncAction } from '@/lib/useAsyncAction'
import { formatCurrency } from '@/lib/format'
import type { Plan } from '@/types'

export interface PlanFormModalProps {
  open: boolean
  onClose: () => void
  /** Present when editing an existing plan. */
  editing: Plan | null
}

export function PlanFormModal({ open, onClose, editing }: PlanFormModalProps): ReactNode {
  const dispatch = useAppDispatch()
  const { isPending, run } = useAsyncAction()
  const [planName, setPlanName] = useState('')
  const [price, setPrice] = useState('0')
  const [durationDays, setDurationDays] = useState('30')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    setPlanName(editing?.plan_name ?? '')
    setPrice(String(editing?.price ?? 0))
    setDurationDays(String(editing?.duration_days ?? 30))
  }, [open, editing])

  const priceValue = Number(price)
  const daysValue = Number(durationDays)
  const errors = {
    name: planName.trim().length < 2 ? 'Give the plan a name.' : undefined,
    price: !Number.isFinite(priceValue) || priceValue < 0 ? 'Must be zero or more.' : undefined,
    days: !Number.isFinite(daysValue) || daysValue < 1 ? 'At least one day.' : undefined
  }
  const valid = Object.values(errors).every((e) => e === undefined)
  const show = (message?: string): string | undefined => (touched ? message : undefined)

  const submit = async (): Promise<void> => {
    setTouched(true)
    if (!valid) return
    const body = {
      plan_name: planName.trim(),
      price: priceValue,
      duration_days: daysValue
    }
    const ok = editing
      ? await run('save', () => dispatch(updatePlan({ id: editing.id, body })).unwrap())
      : await run('save', () => dispatch(createPlan(body)).unwrap())
    if (ok) onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit plan' : 'New plan'}
      description={
        editing
          ? 'Changes apply to future assignments, not to dealers already on this plan.'
          : 'Plans are templates you assign to primary dealers.'
      }
      width="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={isPending('save')}
            onClick={() => void submit()}
          >
            {editing ? 'Save plan' : 'Create plan'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Plan name" error={show(errors.name)}>
          {(id) => (
            <TextInput
              id={id}
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="Professional"
              autoFocus
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Price (INR)" error={show(errors.price)}>
            {(id) => (
              <TextInput
                id={id}
                type="number"
                min={0}
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            )}
          </Field>
          <Field label="Duration (days)" error={show(errors.days)}>
            {(id) => (
              <TextInput
                id={id}
                type="number"
                min={1}
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
              />
            )}
          </Field>
        </div>

        {valid && (
          <p className="rounded-md border border-line bg-raised px-3 py-2 text-[11.5px] text-muted">
            {formatCurrency(priceValue)} for {daysValue} day{daysValue === 1 ? '' : 's'} of access.
          </p>
        )}
      </div>
    </Modal>
  )
}
